import { useEffect, useRef } from 'react';
import dat from 'dat.gui';

/**
 * Vereinfachte GUI-Verwaltung
 */
export const useGUI = (state, callbacks) => {
  const guiRef = useRef(null);

  useEffect(() => {
    // Wenn state oder callbacks null sind, zerstöre GUI und beende
    if (!state || !callbacks) {
      if (guiRef.current) {
        guiRef.current.destroy();
        guiRef.current = null;
      }
      return;
    }

    // Zerstöre alte GUI falls vorhanden
    if (guiRef.current) {
      guiRef.current.destroy();
    }

    // Erstelle neue GUI
    const gui = new dat.GUI();
    guiRef.current = gui;

    // === Export ===
    const exportFolder = gui.addFolder('Export');
    const exportState = {
      exportYOLO: state.exportYOLO,
      showBoundingBoxes: state.showBoundingBoxes,
      maxFrames: state.maxFrames
    };
    exportFolder.add(exportState, 'exportYOLO').onChange(callbacks.setExportYOLO);
    exportFolder.add(exportState, 'showBoundingBoxes').onChange(callbacks.setShowBoundingBoxes);
    exportFolder.add(exportState, 'maxFrames', 1, 1000).onChange(callbacks.setMaxFrames);
    exportFolder.add({ GenerateData: callbacks.startExport }, 'GenerateData');

    // === Canvas ===
    const canvasFolder = gui.addFolder('Canvas');
    const canvasState = {
      width: state.canvasSize.width,
      height: state.canvasSize.height,
      backgroundColor: state.backgroundColor
    };
    canvasFolder.add(canvasState, 'width', 100, 2000).onChange((val) => 
      callbacks.setCanvasSize({ ...state.canvasSize, width: val })
    );
    canvasFolder.add(canvasState, 'height', 100, 2000).onChange((val) => 
      callbacks.setCanvasSize({ ...state.canvasSize, height: val })
    );
    canvasFolder.addColor(canvasState, 'backgroundColor').onChange(callbacks.setBackgroundColor);

    // === Objekt ===
    const objectFolder = gui.addFolder('Object');
    const objectState = { objectType: state.objectType };
    objectFolder.add(objectState, 'objectType', ['model', 'texture']).onChange(callbacks.setObjectType);
    if (state.objectType === 'model') {
      objectFolder.add({ UploadModel: callbacks.uploadModel }, 'UploadModel');
    } else {
      objectFolder.add({ UploadTexture: callbacks.uploadTexture }, 'UploadTexture');
    }

    // === Kamera ===
    const cameraFolder = gui.addFolder('Camera');
    const cameraState = {
      fov: state.camera.fov,
      posX: state.camera.position.x,
      posY: state.camera.position.y,
      posZ: state.camera.position.z
    };
    cameraFolder.add(cameraState, 'fov', 0, 180).onChange((val) =>
      callbacks.setCamera({ ...state.camera, fov: val })
    );
    cameraFolder.add(cameraState, 'posX', -10, 10).onChange((val) =>
      callbacks.setCamera({ ...state.camera, position: { ...state.camera.position, x: val } })
    );
    cameraFolder.add(cameraState, 'posY', -10, 10).onChange((val) =>
      callbacks.setCamera({ ...state.camera, position: { ...state.camera.position, y: val } })
    );
    cameraFolder.add(cameraState, 'posZ', -10, 10).onChange((val) =>
      callbacks.setCamera({ ...state.camera, position: { ...state.camera.position, z: val } })
    );

    // === Spawning (Model) ===
    if (state.objectType === 'model') {
      const spawnFolder = gui.addFolder('Model Spawn');
      const spawnState = {
        minObjects: state.modelSpawn.minObjects,
        maxObjects: state.modelSpawn.maxObjects,
        scale: state.modelSpawn.scale,
        minX: state.modelSpawn.minPosition.x,
        minY: state.modelSpawn.minPosition.y,
        minZ: state.modelSpawn.minPosition.z,
        maxX: state.modelSpawn.maxPosition.x,
        maxY: state.modelSpawn.maxPosition.y,
        maxZ: state.modelSpawn.maxPosition.z
      };
      spawnFolder.add(spawnState, 'minObjects', 1, 10).onChange((val) =>
        callbacks.setModelSpawn({ ...state.modelSpawn, minObjects: val })
      );
      spawnFolder.add(spawnState, 'maxObjects', 1, 10).onChange((val) =>
        callbacks.setModelSpawn({ ...state.modelSpawn, maxObjects: val })
      );
      spawnFolder.add(spawnState, 'scale', 0.001, 0.1).onChange((val) =>
        callbacks.setModelSpawn({ ...state.modelSpawn, scale: val })
      );
      spawnFolder.add(spawnState, 'minX', -10, 10).onChange((val) =>
        callbacks.setModelSpawn({ ...state.modelSpawn, minPosition: { ...state.modelSpawn.minPosition, x: val } })
      );
      spawnFolder.add(spawnState, 'minY', -10, 10).onChange((val) =>
        callbacks.setModelSpawn({ ...state.modelSpawn, minPosition: { ...state.modelSpawn.minPosition, y: val } })
      );
      spawnFolder.add(spawnState, 'minZ', -10, 10).onChange((val) =>
        callbacks.setModelSpawn({ ...state.modelSpawn, minPosition: { ...state.modelSpawn.minPosition, z: val } })
      );
      spawnFolder.add(spawnState, 'maxX', -10, 10).onChange((val) =>
        callbacks.setModelSpawn({ ...state.modelSpawn, maxPosition: { ...state.modelSpawn.maxPosition, x: val } })
      );
      spawnFolder.add(spawnState, 'maxY', -10, 10).onChange((val) =>
        callbacks.setModelSpawn({ ...state.modelSpawn, maxPosition: { ...state.modelSpawn.maxPosition, y: val } })
      );
      spawnFolder.add(spawnState, 'maxZ', -10, 10).onChange((val) =>
        callbacks.setModelSpawn({ ...state.modelSpawn, maxPosition: { ...state.modelSpawn.maxPosition, z: val } })
      );
    }

    // === Licht ===
    const lightFolder = gui.addFolder('Lighting');
    const lightState = {
      ambientColor: state.lighting.ambient.color,
      ambientIntensity: state.lighting.ambient.intensity,
      envPreset: state.lighting.environment.preset
    };
    lightFolder.addColor(lightState, 'ambientColor').onChange((val) =>
      callbacks.setLighting({ ...state.lighting, ambient: { ...state.lighting.ambient, color: val } })
    );
    lightFolder.add(lightState, 'ambientIntensity', 0, 2).onChange((val) =>
      callbacks.setLighting({ ...state.lighting, ambient: { ...state.lighting.ambient, intensity: val } })
    );
    lightFolder.add(lightState, 'envPreset', [
      'warehouse', 'studio', 'lobby', 'city', 'sunset', 'dawn', 'night', 'forest', 'apartment', 'park'
    ]).onChange((val) =>
      callbacks.setLighting({ ...state.lighting, environment: { ...state.lighting.environment, preset: val } })
    );

    // === Randomizer ===
    const randomizerFolder = gui.addFolder('Randomizer');
    const randomizerState = {
      objectPosition: state.randomizers.objectPosition,
      objectRotation: state.randomizers.objectRotation,
      light: state.randomizers.light
    };
    
    randomizerFolder.add(randomizerState, 'objectPosition').onChange((val) =>
      callbacks.setRandomizers({ ...state.randomizers, objectPosition: val })
    );
    randomizerFolder.add(randomizerState, 'objectRotation').onChange((val) =>
      callbacks.setRandomizers({ ...state.randomizers, objectRotation: val })
    );
    randomizerFolder.add(randomizerState, 'light').onChange((val) =>
      callbacks.setRandomizers({ ...state.randomizers, light: val })
    );

    // === Klassen (YOLO) ===
    if (callbacks.classManager) {
      const classesFolder = gui.addFolder('Classes');
      callbacks.classManager.classes.forEach((cls) => {
        const classObj = { name: cls.name };
        classesFolder.add(classObj, 'name').onChange((val) => {
          callbacks.classManager.updateClassName(cls.id, val);
        });
      });
      classesFolder.add({ AddClass: () => {
        const name = prompt('Klassen-Name eingeben:');
        if (name) {
          callbacks.classManager.addClass(name);
          // GUI wird durch useEffect neu erstellt
        }
      }}, 'AddClass');
    }

    return () => {
      // Cleanup: Zerstöre GUI beim Unmount
      if (guiRef.current) {
        guiRef.current.destroy();
        guiRef.current = null;
      }
    };
  }, [state, callbacks]); // eslint-disable-line react-hooks/exhaustive-deps

  return guiRef;
};

